import { useMemo, useState } from 'react';

import { css, styled } from '@mui/material';
import {
  Badge,
  ButtonGhost,
  IconButtonGhost,
  InfoButton,
  Spinner,
  Tabs,
  Typography,
} from '@kc-console/common-ui';
import { useTranslation } from '@kc-console/core';
import { IcChevronDownLargeRegular, IcTrashRegular } from '@kep/kcds-icons';

import { getTaskTabs } from './constants';
import TaskList from './TaskList';

import type { Ref } from 'react';
import type { Task, TaskTabKey } from '@kc-console/context';

interface TaskCounts {
  all: number;
  failed: number;
  in_progress: number;
  success: number;
}

interface TaskManagerDetailProps {
  counts: TaskCounts;
  filterByTab: (tab: TaskTabKey) => Task[];
  onCollapse: () => void;
  onDismiss: (taskId: string) => void;
  onDismissAll: () => void;
  isLoading?: boolean;
  /** 바깥 클릭 감지용 — 펼침 패널 루트에 연결한다(TaskManager가 useClickOutside로 접기). */
  ref?: Ref<HTMLDivElement>;
}

/**
 * 태스크 매니저 펼침 뷰(520×640 고정).
 * @description
 * - 헤더(타이틀+Beta / 성공·실패 제거 / 접기) + 탭(전체·진행중·성공·실패) + 카드 리스트.
 * - 탭 전환은 로컬 상태. 표시 여부·full↔mini 토글은 상위(TaskManager)가 관리한다.
 */
const TaskManagerDetail = ({
  counts,
  filterByTab,
  isLoading = false,
  onCollapse,
  onDismiss,
  onDismissAll,
  ref,
}: TaskManagerDetailProps) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<TaskTabKey>('all');

  const hasDismissable = counts.success + counts.failed > 0;
  const visibleTasks = useMemo(() => filterByTab(tab), [filterByTab, tab]);

  const tabItems = getTaskTabs().map(item => ({
    value: item.value,
    label: (
      <TabLabel>
        {item.label}
        {item.value !== 'all' && counts[item.value] > 0 && (
          <Badge
            color={item.countColor}
            variant="tint"
            shape="circular"
            label={counts[item.value]}
          />
        )}
      </TabLabel>
    ),
  }));

  return (
    <Root ref={ref}>
      <Header>
        <HeaderLeft>
          <Typography variant="label/semibold/xl">
            {t('layout:task_manager.detail.title')}
          </Typography>
          <Badge color="purple" variant="ghost" shape="rectangular" label="Beta" />
          <InfoButton
            alertType="tooltip"
            arrow="down"
            size="sm"
            tooltipProps={{
              message: t('layout:task_manager.detail.beta_guide'),
              wordBreak: 'keep-all',
            }}
          />
        </HeaderLeft>
        <HeaderActions>
          <ButtonGhost
            variant="accent"
            size="sm"
            startIcon={<IcTrashRegular width={16} height={16} />}
            onClick={onDismissAll}
            disabled={!hasDismissable}
          >
            {t('layout:task_manager.detail.clear_completed.label')}
          </ButtonGhost>
          <IconButtonGhost
            variant="primary"
            size="lg"
            onClick={onCollapse}
            aria-label={t('layout:task_manager.detail.collapse.label')}
          >
            <IcChevronDownLargeRegular />
          </IconButtonGhost>
        </HeaderActions>
      </Header>

      <TabBar>
        <Tabs
          size="md"
          value={tab}
          onChange={value => setTab(value as TaskTabKey)}
          items={tabItems}
        />
      </TabBar>

      <Body key={tab}>
        {isLoading ? (
          <LoadingState>
            <Spinner size="lg" />
          </LoadingState>
        ) : (
          <TaskList tasks={visibleTasks} onDismiss={onDismiss} />
        )}
      </Body>
    </Root>
  );
};

export default TaskManagerDetail;

const Root = styled('div')(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    width: 520px;
    height: 640px;
    padding: 16px 24px 24px;
    background-color: ${theme.color['surface/default']};
    border: ${theme.mixins.border('border/weaker/default')};
    border-radius: 4px;
    /* boxShadow/elevation/level 6 */
    box-shadow: ${theme.shadows[6]};
    overflow: hidden;
  `,
);

const Header = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
`;

const HeaderLeft = styled('div')`
  display: flex;
  align-items: center;
`;

const HeaderActions = styled('div')`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const TabBar = styled('div')`
  /* block으로 Tabs가 full-width로 늘어나 하단 라인이 패널 끝까지 그어진다(notification과 동일). */
  display: block;
`;

const TabLabel = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const Body = styled('div')`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;

  /* 투명/얇은 스크롤바 — 트랙은 투명, thumb는 옅은 반투명(hover 시 진해짐). */
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.16) transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.16);
    border-radius: 8px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: rgba(0, 0, 0, 0.28);
  }
`;

const LoadingState = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100%;
`;
